import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'admin' | 'client';
  redirectTo?: string;
}

const ProtectedRoute = ({ 
  children, 
  requiredRole, 
  redirectTo = '/auth' 
}: ProtectedRouteProps) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If no specific role required, allow access
  if (!requiredRole) {
    return <>{children}</>;
  }

  // Check if user has required role
  if (user?.type !== requiredRole) {
    // Determine correct redirect path based on user's actual role
    const userRole = user?.type;
    let redirectPath: string;

    switch (userRole) {
      case 'admin':
        redirectPath = '/admin/dashboard';
        break;
      case 'client':
        redirectPath = '/dashboard';
        break;
      default:
        // Fallback to client dashboard if role is unknown
        redirectPath = '/dashboard';
        break;
    }

    return <Navigate to={redirectPath} replace />;
  }

  // User is authenticated and has required role
  return <>{children}</>;
};

export default ProtectedRoute;
